import { onMounted, onUnmounted, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRoute } from 'vue-router';

// Best-guess starting points chosen pre-launch, without any real usage data.
// Revisit once there's real signal on how staff actually use clock-in/
// my-shifts (shared terminal, walk up and away) vs. back-office routes
// (single user, own device).
export const SHARED_TERMINAL_IDLE_TIMEOUT_MS = 300000; // 5 min — clock-in, my-shifts
export const DEFAULT_IDLE_TIMEOUT_MS = 900000; // 15 min — every other route

const ACTIVITY_EVENTS = ['mousemove', 'scroll', 'keydown', 'click'];

// Mounted once at the app root (App.vue) rather than in Navbar.vue, so the
// idle timer stays armed on every authenticated route regardless of layout
// (e.g. clock-in, my-shifts, setup use layouts that don't render Navbar).
export function useIdleLogout() {
  const { isAuthenticated, logout } = useAuth0();
  const route = useRoute();
  let idleTimeout = null;

  const currentTimeoutMs = () => route.meta?.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;

  const doLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const resetTimer = () => {
    clearTimeout(idleTimeout);
    idleTimeout = setTimeout(doLogout, currentTimeoutMs());
  };

  const attachListeners = () => {
    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, resetTimer));
    resetTimer();
  };

  const detachListeners = () => {
    ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, resetTimer));
    clearTimeout(idleTimeout);
  };

  let unwatchAuth;
  let unwatchRoute;

  onMounted(() => {
    if (isAuthenticated.value) attachListeners();

    unwatchAuth = watch(isAuthenticated, (authed, wasAuthed) => {
      if (authed && !wasAuthed) attachListeners();
      else if (!authed && wasAuthed) detachListeners();
    });

    // Re-arm on every navigation so a route change immediately picks up the
    // destination route's threshold, rather than leaving the previous
    // route's (possibly longer or shorter) countdown running until the next
    // activity event.
    unwatchRoute = watch(() => route.name, () => {
      if (isAuthenticated.value) resetTimer();
    });
  });

  onUnmounted(() => {
    detachListeners();
    if (unwatchAuth) unwatchAuth();
    if (unwatchRoute) unwatchRoute();
  });
}
